import React, { Suspense, lazy, useContext, useEffect, useState } from "react";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IDashboardCoverage } from "../../../services/zeos-api/contracts/Dashboard";
import { ZeosModal } from "../../Shared/ZeosModal";

// Loaded only when someone opens it: keeps the meters panel (and its auth / link-utility imports) out of the
// dashboard's startup, and avoids an import loop with MainDashboard, whose cache helpers the panel calls.
const PropertyMetersPanel = lazy(() =>
  import("../../property/PropertyMetersPanel").then((m) => ({ default: m.PropertyMetersPanel }))
);

// MTR-P2-01 coverage disclosure (task-4051, spec v0.3 R4 / R6 / R10). Shared by the overview Building tile and the
// NextGen detail page. Renders nothing when the API sends no coverage (single-meter property R1, service awaiting
// attestation R8). The caveat is the API's fixed string, shown verbatim and not dismissible; every role sees it.
// The meter-count chip opens the property's meters panel for people who can manage meters (company Admin or super
// admin, per the Phase 1 manageable check) and is plain text for everyone else (no meter administration for
// Executive / Owner).

export const PARTIAL_PERIOD_NOTE = "Includes a meter active for only part of this period.";

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

export const meterChipText = (coverage: IDashboardCoverage) => {
  const noun = (n: number) => (n === 1 ? "meter" : "meters");
  return coverage.expectedMeterCount != null
    ? `${coverage.linkedMeterCount} of ${coverage.expectedMeterCount} ${noun(coverage.expectedMeterCount)}`
    : `${coverage.linkedMeterCount} ${noun(coverage.linkedMeterCount)}`;
};

interface IProps {
  coverage?: IDashboardCoverage | null;
  propertyId?: number;
  /** Needed to decide whether the chip opens the meters panel; without it the chip is plain text. */
  companyId?: number;
  align?: "center" | "left";
  /** One row (chip + caveat) with less spacing, for the fixed-height overview tile. */
  compact?: boolean;
}

export const CoverageDisclosure = ({ coverage, propertyId, companyId, align = "center", compact = false }: IProps) => {
  const api = useContext(ZeosApiContext);
  const [canManage, setCanManage] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const hasCoverage = Boolean(coverage);

  useEffect(() => {
    let cancelled = false;
    setCanManage(false);
    if (!hasCoverage || !propertyId || !companyId) {
      return;
    }
    const loadAsync = async () => {
      try {
        const response = await api.Meter.getManageablePropertyIdsAsync(companyId);
        if (!cancelled && isSuccess(response.zeosStatus)) {
          setCanManage((response.data ?? []).includes(propertyId));
        }
      } catch {
        // Plain-text chip if the check fails; the disclosure itself still shows.
      }
    };
    loadAsync();
    return () => {
      cancelled = true;
    };
  }, [api, hasCoverage, propertyId, companyId]);

  if (!coverage) {
    return null;
  }

  const chip = meterChipText(coverage);
  const alignClass = align === "center" ? "items-center text-center" : "items-start text-left";
  const layoutClass = compact
    ? `flex flex-row flex-wrap justify-center gap-x-2 gap-y-0.5 mt-2 mb-2 px-4 items-center ${align === "center" ? "text-center" : "text-left"}`
    : `flex flex-col gap-1 mt-3 px-4 ${alignClass}`;
  const textClass = compact ? "m-0 " : "";

  return (
    // The overview tile is clickable; keep chip / modal clicks from reaching it.
    <div
      className={layoutClass}
      data-testid="coverage-disclosure"
      onClick={(e) => e.stopPropagation()}
    >
      {canManage && propertyId ? (
        <button
          type="button"
          className="text-xs font-medium rounded-full border border-slate-300 px-2 py-0.5 text-slate-700 hover:border-zeos-orange-dk hover:text-zeos-orange-dk"
          onClick={() => setPanelOpen(true)}
          aria-label={`${chip}: open meters`}
        >
          {chip}
        </button>
      ) : (
        <span className="text-xs font-medium rounded-full border border-slate-200 px-2 py-0.5 text-slate-600">
          {chip}
        </span>
      )}
      {coverage.caveat ? <p className={`${textClass}text-xs text-black/70 max-w-sm`}>{coverage.caveat}</p> : null}
      {coverage.isPartialPeriod ? <p className={`${textClass}text-xs text-black/60 max-w-sm`}>{PARTIAL_PERIOD_NOTE}</p> : null}
      {canManage && propertyId ? (
        <ZeosModal isOpen={panelOpen} onClose={() => setPanelOpen(false)}>
          {panelOpen ? (
            <Suspense fallback={<p className="text-sm text-gray-600">Loading meters…</p>}>
              <PropertyMetersPanel propertyId={propertyId} />
            </Suspense>
          ) : null}
        </ZeosModal>
      ) : null}
    </div>
  );
};
